import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Badge, Ic, QrPattern, Screen } from '../mock/primitives';
import { useContent } from '../siteContent';

function SubscriberScene() {
  return <Screen label="صفحة المشترك: البيانات والباقة وحالة الاشتراك والرصيد وسجل العمليات وبطاقة QR" className="mk-scene mk-subscriber">
    <div className="mk-panel mk-profile">
      <div className="mk-profile-head"><span className="mk-avatar mk-avatar-lg">س</span><span><b>سارة</b><small>05X XXX XX17 · مشتركة نشطة</small></span><Badge status="active" /></div>
      <div className="mk-kv-grid">
        <span><small>الباقة الحالية</small><b>باقة الشهر</b></span>
        <span><small>الوجبات</small><b>وجبتان · 200 جرام</b></span>
        <span><small>البداية</small><b>1 نوفمبر</b></span>
        <span><small>النهاية</small><b>30 نوفمبر</b></span>
        <span><small>الرصيد</small><b>51 وجبة</b></span>
        <span><small>أيام التجميد</small><b>2 من 7</b></span>
      </div>
      <div className="mk-actions-row"><span className="mk-chip">تجميد</span><span className="mk-chip">تجديد</span><span className="mk-chip">إلغاء</span><span className="mk-chip">بطاقة العميل</span></div>
    </div>
    <div className="mk-panel mk-timeline">
      <b className="mk-panel-title">سجل الإجراءات</b>
      <span><i />صرف وجبة · الفرع الرئيسي<small>اليوم</small></span>
      <span><i className="warn" />تجميد يومين<small>الأسبوع الماضي</small></span>
      <span><i className="teal" />تفعيل الاشتراك<small>1 نوفمبر</small></span>
      <div className="mk-mini-qr"><QrPattern /></div>
    </div>
  </Screen>;
}

function OperationsScene() {
  return <Screen label="إعداد الباقات والأحجام والإضافات وإعدادات التوصيل ووقت الإغلاق" className="mk-scene mk-ops">
    <div className="mk-panel">
      <b className="mk-panel-title">باقة الشهر</b>
      <div className="mk-kv"><span>مدة الاشتراك</span><b>26 يومًا</b></div>
      <div className="mk-kv"><span>الوجبات يوميًا</span><b>1 – 3</b></div>
      <small className="mk-label">الأحجام</small>
      <div className="mk-tags"><span>150 جرام</span><span>200 جرام</span><span>250 جرام</span><span className="mk-tag-add">+ حجم</span></div>
      <small className="mk-label">الإضافات</small>
      <div className="mk-tags"><span>مشروب يومي</span><span>سلطة جانبية</span><span className="mk-tag-add">+ إضافة</span></div>
    </div>
    <div className="mk-panel">
      <b className="mk-panel-title">التوصيل</b>
      <small className="mk-label">أيام التوصيل</small>
      <div className="mk-days">{['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'].map((day, index) => <span key={day} className={index < 5 ? 'is-on' : ''}>{day}</span>)}</div>
      <div className="mk-kv"><span>آخر وقت لاستقبال الطلبات</span><b>9:00 م</b></div>
      <div className="mk-row"><span><b>طلب توصيل · غدًا</b><small>حي النرجس · وجبتان</small></span><Badge status="completed">مجدول</Badge></div>
    </div>
  </Screen>;
}

function ControlScene() {
  const stages: [string, number][] = [['جديدة', 4], ['قيد المراجعة', 2], ['معتمدة', 3], ['مفعّلة', 12], ['ملغاة', 1]];
  return <Screen label="حالات طلبات الاشتراك وسجل العمليات والتغييرات" className="mk-scene mk-control">
    <div className="mk-panel">
      <b className="mk-panel-title">طلبات الاشتراك</b>
      <div className="mk-pipeline">{stages.map(([label, count]) => <span key={label}><b>{count}</b><small>{label}</small></span>)}</div>
      <div className="mk-row"><span><b>طلب ملغي · ماجد</b><small>أُلغي مع التسوية</small></span><span className="mk-chip">استعادة الطلب</span></div>
    </div>
    <div className="mk-panel mk-log">
      <b className="mk-panel-title">سجل العمليات</b>
      {[['تعديل باقة', 'مدير المطعم', '10:42 ص'], ['صرف وجبة', 'الكاشير · الفرع الرئيسي', '10:31 ص'], ['تجميد اشتراك', 'خدمة العملاء', '9:58 ص'], ['اعتماد طلب', 'مدير الفرع', '9:20 ص']].map(([action, who, time]) => <div key={action + time} className="mk-log-row"><b>{action}</b><small>{who}</small><small>{time}</small></div>)}
    </div>
  </Screen>;
}

function InsightScene() {
  return <Screen label="الفواتير وسجلات العمليات المالية" className="mk-scene mk-insight">
    <div className="mk-panel">
      <b className="mk-panel-title">الفواتير</b>
      {[['INV-1042', 'اشتراك · باقة الشهر', 'paid'], ['INV-1041', 'تجديد · باقة الأسبوعين', 'paid'], ['INV-1040', 'اشتراك · باقة الأسبوع', 'pending']].map(([code, detail, status]) => <div className="mk-row" key={code}><span><b>{code}</b><small>{detail}</small></span><Badge status={status === 'paid' ? 'completed' : 'pending'}>{status === 'paid' ? 'مدفوعة' : 'بانتظار الدفع'}</Badge></div>)}
    </div>
    <div className="mk-panel mk-spark">
      <b className="mk-panel-title">الصرف هذا الأسبوع</b>
      <div className="mk-bars">{[52, 64, 58, 71, 69, 80, 44].map((value, index) => <i key={index} style={{ '--v': value } as React.CSSProperties} />)}</div>
      <div className="mk-kv"><span><Ic name="download" /> تصدير التقرير</span><b>PDF</b></div>
    </div>
  </Screen>;
}

const scenes: ReactNode[] = [<SubscriberScene key="subs" />, <OperationsScene key="ops" />, <ControlScene key="control" />, <InsightScene key="insight" />];

export function Features() {
  const { features } = useContent();
  const groups = features.groups.slice(0, scenes.length).map((group, index) => ({ ...group, id: `g${index}`, scene: scenes[index] }));
  const [active, setActive] = useState(0);
  const base = useId();
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    // RTL: the next tab sits to the left.
    const keys: Record<string, number> = { ArrowLeft: 1, ArrowRight: -1, Home: -index, End: groups.length - 1 - index };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const next = (index + keys[event.key] + groups.length) % groups.length;
    setActive(next); tabs.current[next]?.focus();
  };

  if (!features.visible || !groups.length) return null;
  return <section id="features" className="mx-features" aria-labelledby="features-title">
    <div className="mx-container">
      <header className="mx-section-head mx-section-head-split">
        <h2 id="features-title" className="mx-h2"><span>{features.line1}</span><span>{features.line2}</span></h2>
        <p className="mx-body">{features.body}</p>
      </header>
      <div className="mx-tabs" role="tablist" aria-label="مجموعات المميزات">
        {groups.map((group, index) => <button key={group.id} ref={node => { tabs.current[index] = node; }} role="tab" type="button" id={`${base}-tab-${group.id}`} aria-selected={active === index} aria-controls={`${base}-panel-${group.id}`} tabIndex={active === index ? 0 : -1} className="mx-tab" onClick={() => setActive(index)} onKeyDown={event => onKey(event, index)}>
          <b>{group.title}</b><small>{group.items}</small>
        </button>)}
      </div>
      {groups.map((group, index) => <div key={group.id} role="tabpanel" id={`${base}-panel-${group.id}`} aria-labelledby={`${base}-tab-${group.id}`} hidden={active !== index} className="mx-tabpanel">
        <div className="mx-tabpanel-copy">
          <h3 className="mx-h3">{group.heading}</h3>
          <p className="mx-body">{group.text}</p>
          <ul className="mx-ticks">{group.points.map(point => <li key={point}>{point}</li>)}</ul>
        </div>
        <div className="mx-tabpanel-scene">{group.scene}</div>
      </div>)}
    </div>
  </section>;
}
